import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  TextField,
} from "@mui/material";
import { Icon } from "@mui/material";
import { OrderResponse } from "../../types/pos.types";

interface ReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  onPrint: () => void;
  onEmail: (email: string) => void;
  receipt: OrderResponse;
}

export const ReceiptDialog: React.FC<ReceiptDialogProps> = ({
  open,
  onClose,
  onPrint,
  onEmail,
  receipt,
}) => {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [showEmailField, setShowEmailField] = useState(false);

  const formatCurrency = (amount: number) => {
    return `BHD ${amount.toFixed(2)}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleEmailClick = () => {
    setShowEmailField(true);
  };

  const handleEmailSubmit = () => {
    if (email.trim()) {
      onEmail(email.trim());
      setShowEmailField(false);
      setEmail("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ color: "primary.main" }}>receipt_long</Icon>
          <Typography variant="h6">Order Receipt</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {showEmailField ? (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="Enter email address"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowEmailField(false);
                  setEmail("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleEmailSubmit}
                disabled={!email.trim()}
              >
                Send
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Order #{receipt.order_number}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(receipt.created_at)}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              {receipt.order_lines.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    py: 0.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box>
                    <Typography variant="body1">{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.quantity} x {formatCurrency(item.price)}
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {formatCurrency(item.item_revenue)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body1">Subtotal</Typography>
                <Typography variant="body1">
                  {formatCurrency(receipt.subtotal)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body1">Tax</Typography>
                <Typography variant="body1">
                  {formatCurrency(receipt.tax)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: `1px solid ${theme.palette.divider}`,
                  pt: 1,
                }}
              >
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6">
                  {formatCurrency(receipt.total)}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      {!showEmailField && (
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            startIcon={<Icon>close</Icon>}
          >
            Close
          </Button>
          <Button
            variant="outlined"
            onClick={handleEmailClick}
            startIcon={<Icon>email</Icon>}
          >
            Email
          </Button>
          <Button
            variant="contained"
            onClick={onPrint}
            startIcon={<Icon>print</Icon>}
          >
            Print
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
